import { useState, useEffect } from 'react';
import { fabric } from 'fabric';
import { SequenceInfo } from '../../Annotation/annotation.types';

export const useCanvasEvents = (
  canvasEl: fabric.Canvas | null,
  sequenceInfo: SequenceInfo | null,
  objectModifiedHandler: (activeObjects: fabric.Object[], skipSmallMovements: boolean) => void,
  onSelectAnnotation: (newSequencePath: string, imageName: string, id: string) => void,
  onImageObjectSelected: (obj?: fabric.Object) => void
) => {
  const [isDragging, setIsDragging] = useState(false);
  const [lastPosX, setLastPosX] = useState(0);
  const [lastPosY, setLastPosY] = useState(0);
  const [isPanning, setIsPanning] = useState(false);

  useEffect(() => {
    if (!canvasEl) return;

    canvasEl.defaultCursor = isPanning ? 'grabbing' : 'grab';
    canvasEl.selectionColor = 'transparent';
    canvasEl.selectionBorderColor = 'transparent';

    canvasEl
      .on('object:modified', e => {
        if (e.target) {
          const activeObjects = canvasEl.getActiveObjects();

          objectModifiedHandler(activeObjects, false);
        }
      })
      .on('mouse:wheel', opt => {
        const event = opt.e as WheelEvent;
        const delta = event.deltaY > 0 ? 20 : -20;
        let zoom = canvasEl.getZoom();
        zoom = zoom - delta / 1000;
        if (zoom > 10) zoom = 10;
        if (zoom < 0.01) zoom = 0.01;
        canvasEl!.zoomToPoint(new fabric.Point(event.offsetX, event.offsetY), zoom);
        opt.e.preventDefault();
        opt.e.stopPropagation();
      })
      .on('mouse:down', opt => {
        if (opt.target) {
          if (opt.target.name && opt.target.type === 'image') {
            onImageObjectSelected(opt.target);
          } else {
            if (sequenceInfo) {
              const annotations = sequenceInfo.images.map(x => x.annotations).flat();
              if (annotations) {
                const selected = annotations.find(x => x.id === opt.target!.name);
                if (selected) {
                  onSelectAnnotation(selected.sequencePath, selected.imageName, selected.id);
                }
              }
            }
          }
          canvasEl.selection = true;
        } else {
          canvasEl.selection = false;
          onImageObjectSelected(undefined);
        }

        const event = opt.e as WheelEvent;
        if (event.ctrlKey) {
          setLastPosX(event.clientX);
          setLastPosY(event.clientY);
        }
        setIsPanning(true);
      })
      .on('mouse:move', opt => {
        if (isDragging) {
          const event = opt.e as WheelEvent;
          canvasEl.viewportTransform![4] += event.clientX - lastPosX;
          canvasEl.viewportTransform![5] += event.clientY - lastPosY;
          canvasEl.requestRenderAll();
          setLastPosX(event.clientX);
          setLastPosY(event.clientY);
        }

        if (isPanning && opt && opt.e && !canvasEl.getActiveObject()) {
          const mouseEvent = opt.e as MouseEvent;
          var delta = new fabric.Point(mouseEvent.movementX, mouseEvent.movementY);
          canvasEl.relativePan(delta);
        }
      })
      .on('mouse:up', opt => {
        setIsDragging(false);
        setIsPanning(false);
      });

    return () => {
      canvasEl.off('mouse:wheel').off('mouse:down').off('mouse:move').off('mouse:up').off('object:modified');
    };
  }, [canvasEl, isDragging, isPanning, lastPosX, lastPosY, objectModifiedHandler, onImageObjectSelected, onSelectAnnotation, sequenceInfo]);
};

export default useCanvasEvents;
